import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  alpha,
  useTheme,
  Skeleton,
  IconButton,
  Grid,
  Tooltip,
  Card,
  CardContent,
  Avatar,
  List,
  ListItem,
  Divider,
  CircularProgress,
  Tab,
  Tabs,
  TablePagination,
  Alert,
} from '@mui/material';
import {
  Receipt,
  Download,
  CheckCircle,
  Warning,
  AccessTime,
  Pause,
  Cancel,
  Payment,
  PlayArrow,
  History,
  CreditCard,
  CalendarMonth,
  TrendingUp,
  LocalAtm,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import billingService, {
  BillingOverview,
  Payment as PaymentType,
  SubscriptionHistoryItem,
} from '../services/billingService';
import { usePlatformInvoicePdf } from '../components/billing/usePlatformInvoicePdf';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`billing-tabpanel-${index}`}
      aria-labelledby={`billing-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const BillingPage: React.FC = () => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // State
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [payments, setPayments] = useState<PaymentType[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [history, setHistory] = useState<SubscriptionHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyFetched, setHistoryFetched] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  
  // Pagination
  const [paymentsPage, setPaymentsPage] = useState(0);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const rowsPerPage = 10;

  // Download state
  const invoices = usePlatformInvoicePdf();
  const downloadingInvoiceId = invoices.busyId;

  // Track last fetched page to prevent duplicate calls
  const [lastPaymentsPage, setLastPaymentsPage] = useState<number | null>(null);
  const [lastHistoryPage, setLastHistoryPage] = useState<number | null>(null);

  // Initial load - only overview and payments (page 0)
  useEffect(() => {
    const loadInitialData = async () => {
      setLoading(true);
      try {
        const [overviewData, paymentsData] = await Promise.all([
          billingService.getBillingOverview(),
          billingService.getMyPayments({ page: 0, limit: rowsPerPage })
        ]);
        setOverview(overviewData);
        setPayments(paymentsData.data);
        setPaymentsTotal(paymentsData.pagination.total);
        setLastPaymentsPage(0);
      } catch (error: any) {
        enqueueSnackbar(error.response?.data?.message || 'Failed to load billing data', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };
    loadInitialData();
  }, [enqueueSnackbar]);

  // Fetch payments when page changes
  useEffect(() => {
    // Skip if same page or initial load not done
    if (lastPaymentsPage === null || paymentsPage === lastPaymentsPage) return;
    
    const fetchPayments = async () => {
      setPaymentsLoading(true);
      try {
        const response = await billingService.getMyPayments({ page: paymentsPage, limit: rowsPerPage });
        setPayments(response.data);
        setPaymentsTotal(response.pagination.total);
        setLastPaymentsPage(paymentsPage);
      } catch (error: any) {
        enqueueSnackbar(error.response?.data?.message || 'Failed to load payments', { variant: 'error' });
      } finally {
        setPaymentsLoading(false);
      }
    };
    fetchPayments();
  }, [paymentsPage, lastPaymentsPage, enqueueSnackbar]);

  // Fetch history when tab is clicked (lazy load)
  useEffect(() => {
    if (tabValue !== 1 || historyFetched) return;
    
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const response = await billingService.getMySubscriptionHistory({ page: 0, limit: rowsPerPage });
        setHistory(response.data);
        setHistoryTotal(response.pagination.total);
        setHistoryFetched(true);
        setLastHistoryPage(0);
      } catch (error: any) {
        enqueueSnackbar(error.response?.data?.message || 'Failed to load history', { variant: 'error' });
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();
  }, [tabValue, historyFetched, enqueueSnackbar]);

  // Fetch history when page changes
  useEffect(() => {
    // Skip if same page or not yet fetched
    if (lastHistoryPage === null || historyPage === lastHistoryPage) return;
    
    const fetchHistory = async () => {
      setHistoryLoading(true);
      try {
        const response = await billingService.getMySubscriptionHistory({ page: historyPage, limit: rowsPerPage });
        setHistory(response.data);
        setHistoryTotal(response.pagination.total);
        setLastHistoryPage(historyPage);
      } catch (error: any) {
        enqueueSnackbar(error.response?.data?.message || 'Failed to load history', { variant: 'error' });
      } finally {
        setHistoryLoading(false);
      }
    };
    fetchHistory();
  }, [historyPage, lastHistoryPage, enqueueSnackbar]);

  // Handle invoice download
  // The invoice is drawn in the browser; the button spins while it does.
  const handleDownloadInvoice = (payment: PaymentType) =>
    invoices.download(payment._id, payment.invoiceNumber, () => billingService.getInvoice(payment._id));


  // Status chip render
  const renderStatusChip = (status: string) => {
    const config: Record<string, { color: string; icon: React.ReactNode; label?: string }> = {
      active: { color: theme.palette.success.main, icon: <CheckCircle sx={{ fontSize: 12 }} /> },
      trial: { color: theme.palette.info.main, icon: <AccessTime sx={{ fontSize: 12 }} /> },
      expired: { color: theme.palette.error.main, icon: <Warning sx={{ fontSize: 12 }} /> },
      cancelled: { color: theme.palette.grey[500], icon: <Cancel sx={{ fontSize: 12 }} /> },
      suspended: { color: theme.palette.warning.main, icon: <Pause sx={{ fontSize: 12 }} /> },
      pending_payment: { color: theme.palette.warning.dark, icon: <Payment sx={{ fontSize: 12 }} />, label: 'Pending' },
    };
    const { color, icon, label } = config[status] || config.active;

    return (
      <Chip
        icon={icon as any}
        label={label || (status.charAt(0).toUpperCase() + status.slice(1))}
        size="small"
        sx={{
          height: 22,
          fontSize: '0.7rem',
          bgcolor: alpha(color, 0.1),
          color: color,
          fontWeight: 600,
          '& .MuiChip-icon': { color: color, fontSize: 12 },
        }}
      />
    );
  };

  // Get action label based on history action
  const getHistoryActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      created: 'Created',
      plan_changed: 'Plan Changed',
      billing_cycle_changed: 'Cycle Changed',
      extended: 'Extended',
      suspended: 'Suspended',
      resumed: 'Resumed',
      cancelled: 'Cancelled',
      expired: 'Expired',
      renewed: 'Renewed',
    };
    return labels[action] || action;
  };

  // Get action color based on history action
  const getHistoryActionColor = (action: string) => {
    const colors: Record<string, string> = {
      created: theme.palette.success.main,
      plan_changed: theme.palette.info.main,
      billing_cycle_changed: theme.palette.info.main,
      extended: theme.palette.success.main,
      suspended: theme.palette.warning.main,
      resumed: theme.palette.success.main,
      cancelled: theme.palette.error.main,
      expired: theme.palette.error.main,
      renewed: theme.palette.success.main,
    };
    return colors[action] || theme.palette.grey[500];
  };

  // Loading skeleton
  if (loading) {
    return (
      <Box>
        <Skeleton variant="text" width={200} height={32} sx={{ mb: 1 }} />
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid size={{ xs: 6, md: 3 }} key={i}>
              <Skeleton variant="rounded" height={88} />
            </Grid>
          ))}
        </Grid>
        <Skeleton variant="rounded" height={300} />
      </Box>
    );
  }

  const daysRemaining = overview?.subscription?.daysRemaining || 0;
  const isExpiring = daysRemaining > 0 && daysRemaining <= 7;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h5" fontWeight={700} color="text.primary">
          Billing & Invoices
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          View subscription details and download invoices
        </Typography>
      </Box>

      {/* Subscription Warning */}
      {overview?.subscription?.isExpired && (
        <Alert severity="error" sx={{ mb: 2, py: 0.5 }} icon={<Warning sx={{ fontSize: 18 }} />}>
          <Typography variant="body2">
            Subscription expired. Contact administrator to renew.
          </Typography>
        </Alert>
      )}

      {isExpiring && !overview?.subscription?.isExpired && (
        <Alert severity="warning" sx={{ mb: 2, py: 0.5 }} icon={<AccessTime sx={{ fontSize: 18 }} />}>
          <Typography variant="body2">
            Your subscription expires in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}. Please contact support to renew.
          </Typography>
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        {/* Current Plan */}
        <Grid size={{ xs: 6, lg: 3 }}>
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                  }}
                >
                  <CreditCard sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                </Avatar>
                {overview?.subscription && renderStatusChip(overview.subscription.status)}
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Current Plan
              </Typography>
              <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.3 }}>
                {overview?.plan?.name || 'No Plan'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {overview?.subscription?.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Days Remaining */}
        <Grid size={{ xs: 6, lg: 3 }}>
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ mb: 1.5 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: alpha(
                      overview?.subscription?.isExpired 
                        ? theme.palette.error.main 
                        : isExpiring 
                          ? theme.palette.warning.main 
                          : theme.palette.success.main,
                      0.1
                    ),
                  }}
                >
                  <CalendarMonth
                    sx={{
                      fontSize: 18,
                      color: overview?.subscription?.isExpired
                        ? theme.palette.error.main
                        : isExpiring
                          ? theme.palette.warning.main
                          : theme.palette.success.main,
                    }}
                  />
                </Avatar>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Days Remaining
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                sx={{ lineHeight: 1.3 }}
                color={
                  overview?.subscription?.isExpired
                    ? 'error.main'
                    : isExpiring
                      ? 'warning.main'
                      : 'text.primary'
                }
              >
                {overview?.subscription?.isExpired ? 'Expired' : daysRemaining}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {overview?.subscription?.endDate 
                  ? `Expires on ${format(new Date(overview.subscription.endDate), 'MMM dd, yyyy')}`  
                  : 'N/A'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Payments */}
        <Grid size={{ xs: 6, lg: 3 }}>
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ mb: 1.5 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: alpha(theme.palette.info.main, 0.1),
                  }}
                >
                  <Receipt sx={{ color: theme.palette.info.main, fontSize: 18 }} />
                </Avatar>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Total Payments
              </Typography>
              <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.3 }}>
                {overview?.stats?.totalPayments || 0}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Invoices
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Total Paid */}
        <Grid size={{ xs: 6, lg: 3 }}>
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
            }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ mb: 1.5 }}>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                  }}
                >
                  <LocalAtm sx={{ color: theme.palette.success.main, fontSize: 18 }} />
                </Avatar>
              </Box>
              <Typography variant="caption" color="text.secondary" display="block">
                Total Paid
              </Typography>
              <Typography variant="h6" fontWeight={700} color="text.primary" sx={{ lineHeight: 1.3 }}>
                PKR {(overview?.stats?.totalPaid || 0).toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Lifetime
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 1 }}>
          <Tabs
            value={tabValue}
            onChange={(_, newValue) => setTabValue(newValue)}
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.85rem',
                minHeight: 44,
                py: 0,
              },
            }}
          >
            <Tab 
              icon={<Receipt sx={{ fontSize: 18 }} />} 
              iconPosition="start" 
              label="Payment History" 
            />
            <Tab 
              icon={<History sx={{ fontSize: 18 }} />} 
              iconPosition="start" 
              label="Subscription History" 
            />
          </Tabs>
        </Box>

        {/* Payment History Tab */}
        <TabPanel value={tabValue} index={0}>
          {paymentsLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : payments.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Receipt sx={{ fontSize: 48, color: 'text.disabled', mb: 1, opacity: 0.4 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                No payments yet
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Payment history will appear here
              </Typography>
            </Box>
          ) : (
            <>
              <List sx={{ py: 0, opacity: paymentsLoading ? 0.5 : 1 }}>
                {payments.map((payment, index) => (
                  <React.Fragment key={payment._id}>
                    <ListItem
                      sx={{
                        py: 1.5,
                        px: 2,
                        gap: 1.5,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                        }}
                      >
                        <Receipt sx={{ color: theme.palette.success.main, fontSize: 18 }} />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight={600}>
                            PKR {payment.amount.toLocaleString()}
                          </Typography>
                          <Chip
                            label={payment.paymentMethod.replace('_', ' ').toUpperCase()}
                            size="small"
                            sx={{ 
                              fontSize: '0.65rem', 
                              height: 20,
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: theme.palette.primary.main,
                              fontWeight: 600,
                            }}
                          />
                          <Chip
                            label={payment.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {payment.invoiceNumber} • {format(new Date(payment.paymentDate), 'MMM dd, yyyy')}
                        </Typography>
                      </Box>
                      <Tooltip title="Download Invoice">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDownloadInvoice(payment)}
                            disabled={downloadingInvoiceId === payment._id}
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.08),
                              '&:hover': {
                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                              },
                            }}
                          >
                            {downloadingInvoiceId === payment._id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <Download sx={{ color: theme.palette.primary.main, fontSize: 18 }} />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </ListItem>
                    {index < payments.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
              <TablePagination
                component="div"
                count={paymentsTotal}
                page={paymentsPage}
                onPageChange={(_, newPage) => setPaymentsPage(newPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10]}
                sx={{ 
                  borderTop: `1px solid ${theme.palette.divider}`,
                  '& .MuiTablePagination-toolbar': { minHeight: 44 },
                }}
              />
            </>
          )}
        </TabPanel>

        {/* Subscription History Tab */}
        <TabPanel value={tabValue} index={1}>
          {historyLoading && history.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : history.length === 0 && historyFetched ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <History sx={{ fontSize: 48, color: 'text.disabled', mb: 1, opacity: 0.4 }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                No history yet
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Subscription changes will appear here
              </Typography>
            </Box>
          ) : history.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <List sx={{ py: 0, opacity: historyLoading ? 0.5 : 1 }}>
                {history.map((item, index) => (
                  <React.Fragment key={item._id}>
                    <ListItem
                      sx={{
                        py: 1.5,
                        px: 2,
                        gap: 1.5,
                        // '&:hover': {
                        //   bgcolor: alpha(theme.palette.action.hover, 0.5),
                        // },
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 36,
                          height: 36,
                          bgcolor: alpha(getHistoryActionColor(item.action), 0.1),
                        }}
                      >
                        {item.action === 'extended' || item.action === 'renewed' ? (
                          <TrendingUp sx={{ color: getHistoryActionColor(item.action), fontSize: 18 }} />
                        ) : item.action === 'suspended' ? (
                          <Pause sx={{ color: getHistoryActionColor(item.action), fontSize: 18 }} />
                        ) : item.action === 'resumed' ? (
                          <PlayArrow sx={{ color: getHistoryActionColor(item.action), fontSize: 18 }} />
                        ) : item.action === 'cancelled' || item.action === 'expired' ? (
                          <Cancel sx={{ color: getHistoryActionColor(item.action), fontSize: 18 }} />
                        ) : (
                          <CheckCircle sx={{ color: getHistoryActionColor(item.action), fontSize: 18 }} />
                        )}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {getHistoryActionLabel(item.action)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {format(new Date(item.createdAt), 'MMM dd, yyyy • hh:mm a')}
                          {item.notes && ` • ${item.notes}`}
                        </Typography>
                      </Box>
                      <Chip
                        size="small"
                        label={item.action.replace('_', ' ')}
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          bgcolor: alpha(getHistoryActionColor(item.action), 0.1),
                          color: getHistoryActionColor(item.action),
                          fontWeight: 600,
                          textTransform: 'capitalize',
                        }}
                      />
                    </ListItem>
                    {index < history.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                ))}
              </List>
              <TablePagination
                component="div"
                count={historyTotal}
                page={historyPage}
                onPageChange={(_, newPage) => setHistoryPage(newPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10]}
                sx={{ 
                  borderTop: `1px solid ${theme.palette.divider}`,
                  '& .MuiTablePagination-toolbar': { minHeight: 44 },
                }}
              />
            </>
          )}
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default BillingPage;
